import config from '/src/config';

const priorityTypes = [
    'street_number',
    'route',
    'subpremise',
    'locality',
    'administrative_area_level_1',
    'administrative_area_level_2',
    'postal_code'
];

/**
 * Gets the user's input from an autocomplete input field
 * @param {HTMLInputElement} inputElement The autocomplete input element
 * @returns {string} The current value of the input field
 */
function getUserInputFromAutocomplete(inputElement) {
    return inputElement ? inputElement.value : '';
}

/**
 * Serializes an address object to a string
 * @param {object<address>} address Address object that will be serialized to a string
 * @param {string} address.addressLine1 Typically number and street address, but could be c/o or attn line
 * @param {string} address.addressLine2 Not typically used, but could be number/street address if line 1 was used
 * @param {string} address.city City
 * @param {string} address.state State abbreviation
 * @param {string} address.zipcode Zipcode
 * @returns a string representation of the Address object
 */
function addressToString(address) {
    const newAddress = Object.assign({}, address)
    delete newAddress.state
    if (!Object.keys(newAddress).length) return ''
    const parts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.stateAbbrev || address.state,
      address.zipcode
    ].filter(part => part && part.trim());

    return parts.join(' ');
}

function parseAddressFromGoogleObject(googleData, userData = '') {
    try {
        if (googleData.address_components) {
            const addressData = googleData.address_components.reduce((data, component) => {
                const relevantType = component.types.find(type => priorityTypes.includes(type)) || component.types[0];
                if (relevantType === 'subpremise') {
                    component.long_name = component.long_name.toLowerCase()
                        .split(' ')
                        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
                        .join(' ');
                }
                data[relevantType] = relevantType === 'administrative_area_level_1' ? component.short_name : component.long_name
                return data
            }, {})

            if (!addressData['street_number']) addressData['street_number'] = ''
            if (!addressData['route']) addressData['route'] = ''
            if (!addressData['subpremise']) addressData['subpremise'] = ''
            if (!addressData['postal_code']) addressData['postal_code'] = ''
            if (!addressData['locality']) addressData['locality'] = ''
            if (!addressData['administrative_area_level_3']) addressData['administrative_area_level_3'] = ''

            const formattedAddress = googleData.formatted_address || userData || '';
            const addressParts = formattedAddress.split(',').map(part => part.trim());

            return {
                addressLine1: addressData['street_number'] ? (`${addressData['street_number']} ${addressData['route']} ${addressData['subpremise']}`).trim() : (addressParts[0] || ''),
                addressLine2: '',
                city: addressData['locality'] || addressData['administrative_area_level_3'] || addressData['neighborhood'] || (addressParts[1] || ''),
                state: addressData['administrative_area_level_1'],
                county: addressData['administrative_area_level_2'],
                zipcode: addressData['postal_code'],
            }
        } else {
            console.warn('No address_components found, falling back to user input');
            const addressSplit = googleData.name.split(/Drive,? |Dr,? |Road,? |Rd,? |Street,? |St,? |Lane,? |Ln,? |Ave,? |Avenue,? |Parkway,? |Pkwy,? |Court,? |Ct,? |Circle,? |Cir,? |Bend,? |Bnd,? |Grove,? |Grv,? |Way,? |Row,? |Boulevard,? |Blvd,? [A-Za-z]{2,} [A-Z]{2} \d{5}/)
            const location = addressSplit[addressSplit.length - 1];
            const locationSplit = location.split(/([A-Z]{2})/)
            const streetAddress = googleData.name.slice(0, googleData.name.indexOf(location));
            return {
                addressLine1: streetAddress.trim() || '',
                addressLine2: '',
                city: locationSplit[0].trim() || '',
                state: locationSplit[1].trim() || '',
                county: '',
                zipcode: locationSplit[2].trim() || '',
            }
        }
    } catch (error) {
        console.warn('Failed to parse address', error);
        return {
            addressLine1: userData || '',
            addressLine2: '',
            city: '',
            state: '',
            county: '',
            zipcode: '',
        }
    }
}

async function verifyAddress(addressString) {
    const response = await fetch(`https://addressvalidation.googleapis.com/v1:validateAddress?alt=json&key=${config.GOOGLE_MAP_KEY}`, {
        method: 'POST',
        body: JSON.stringify({ address: { regionCode: "US", addressLines: [addressString] } })
    });

    if (!response.ok) return null;

    const data = await response.json();
    const googleAddress = data.result.address.postalAddress;

    if (!data.result.verdict.addressComplete) return null;

    return {
        addressLine1: googleAddress.addressLines[0],
        addressLine2: googleAddress.addressLines[1] ?? '',
        city: googleAddress.locality ?? '',
        state: googleAddress.administrativeArea ?? '',
        county: '',
        zipcode: googleAddress.postalCode ?? '',
    };
}

export default { addressToString, parseAddressFromGoogleObject, verifyAddress, getUserInputFromAutocomplete };
